import axios from "axios";
import { getCookie } from "../utils/auth";

// ==========================================
// BUILD BASE URL
// ==========================================
let BASE =
  process.env.REACT_APP_BACKEND_ROOT ||
  process.env.REACT_APP_BACKEND_URL ||
  "http://localhost:5001";

BASE = BASE.replace(/\/+$/, "");

if (!BASE.endsWith("/api/v1")) {
  if (BASE.endsWith("/api")) BASE += "/v1";
  else BASE += "/api/v1";
}

const API_URL = `${BASE}/applications`;
console.log("🔥 FINAL API BASE:", API_URL);

// ==========================================
// AUTH HEADERS
// ==========================================
const getAuthHeaders = () => {
  const token = getCookie("auth_token");
  if (!token) throw new Error("Authentication required");
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };
};

const getMultipartHeaders = () => {
  const token = getCookie("auth_token");
  if (!token) throw new Error("Authentication required");
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

// ==========================================
// ERROR HANDLER
// ==========================================
const handleError = async (error) => {
  let message = "Something went wrong";

  if (error.response?.data instanceof Blob) {
    try {
      const text = await error.response.data.text();
      const json = JSON.parse(text);
      message = json.message || message;
    } catch {}
  } else if (error.response?.data?.message) {
    message = error.response.data.message;
  } else if (error.message) {
    message = error.message;
  }

  if (error.response?.status === 401) {
    throw new Error("Session expired. Please login again.");
  }

  // Enhanced error logging
  console.error('API Error Details:', {
    status: error.response?.status,
    statusText: error.response?.statusText,
    data: error.response?.data,
    message: message,
    url: error.config?.url,
    method: error.config?.method
  });

  throw new Error(message);
};

// ==========================================
// DOWNLOAD HELPER
// ==========================================
const downloadHelper = (blob, filename) => {
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// ==========================================
// APPLICATION API
// ==========================================
export const formFillApi = {
  // -------------------------
  // PRICING
  // -------------------------
  // FIX: registrationType param added so backend filter matches DB field correctly.
  // Tries 'application_fee' first (matches _id: 69de07432153c13100f8275b),
  // fallback to 'student_registration' (matches _id: 69de06212153c13100f8275a).
// -------------------------
// PRICING
// -------------------------
getApplicationPricing: async (
  registrationType = "student_registration"
) => {
  try {
    const res = await axios.get(
      `${API_URL}/pricing?registrationType=${encodeURIComponent(registrationType)}`,
      getAuthHeaders()
    );

    return res.data;
  } catch (error) {
    await handleError(error);
  }
},

  // -------------------------
  // COUPON VALIDATION
  // -------------------------
  validateCoupon: async (payload) => {
    try {
      console.log('🎫 Validating coupon:', payload);
      
      const res = await axios.post(
        `${API_URL}/validate-coupon`,
        payload,
        getAuthHeaders()
      );
      
      console.log('✅ Coupon validation response:', res.data);
      return res.data;
    } catch (error) {
      console.error('❌ Coupon validation failed:', error);
      
      // Extract error message properly
      let errorMessage = 'Failed to validate coupon';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  },

  // -------------------------
  // DRAFT (ENHANCED)
  // -------------------------
  saveDraft: async (data) => {
    try {
      console.log('📤 Sending draft payload:', JSON.stringify(data, null, 2));
      
      // Clean the payload - remove any undefined or empty nested objects
      const cleanPayload = JSON.parse(JSON.stringify(data, (key, value) => {
        // Keep null values but remove undefined
        if (value === undefined) return null;
        return value;
      }));

      console.log('🧹 Cleaned payload:', JSON.stringify(cleanPayload, null, 2));

      const res = await axios.post(
        `${API_URL}/draft`, 
        cleanPayload, 
        {
          ...getAuthHeaders(),
          timeout: 30000 // 30 second timeout
        }
      );
      
      console.log('✅ Draft saved successfully:', res.data);
      return res.data;
    } catch (error) {
      console.error('❌ Save draft failed:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Try to extract meaningful error message
      let errorMessage = 'Failed to save draft';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.errors) {
        // Handle validation errors
        const errors = error.response.data.errors;
        if (Array.isArray(errors)) {
          errorMessage = errors.map(e => e.msg || e.message).join(', ');
        } else if (typeof errors === 'object') {
          errorMessage = Object.values(errors).join(', ');
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      throw new Error(errorMessage);
    }
  },

  // -------------------------
  // DOCUMENT UPLOAD
  // -------------------------
  uploadDocument: async (file, documentType) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("documentType", documentType);

      console.log(`📎 Uploading ${documentType}...`);

      const res = await axios.post(
        `${API_URL}/upload-doc`,
        fd,
        getMultipartHeaders()
      );
      
      console.log('✅ Upload successful:', res.data);
      return res.data;
    } catch (error) {
      console.error('❌ Upload failed:', error);
      await handleError(error);
    }
  },

  // -------------------------
  // PAYMENT
  // -------------------------
  createPaymentOrder: async (payload) => {
    try {
      const res = await axios.post(
        `${API_URL}/create-payment-order`,
        payload,
        getAuthHeaders()
      );
      return res.data;
    } catch (error) {
      await handleError(error);
    }
  },

  verifyAndSubmit: async (payload) => {
    try {
      const res = await axios.post(
        `${API_URL}/verify-payment`,
        payload,
        getAuthHeaders()
      );
      return res.data;
    } catch (error) {
      await handleError(error);
    }
  },

  // -------------------------
  // APPLICATION LIST
  // -------------------------
  getMyApplications: async () => {
    try {
      const res = await axios.get(
        `${API_URL}/my-applications`,
        getAuthHeaders()
      );
      return res.data;
    } catch (error) {
      // Don't throw error if no applications exist yet
      if (error.response?.status === 404) {
        return { success: true, applications: [] };
      }
      await handleError(error);
    }
  },

  getApplicationById: async (id) => {
    try {
      const res = await axios.get(`${API_URL}/${id}`, getAuthHeaders());
      return res.data;
    } catch (error) {
      await handleError(error);
    }
  },

  // -------------------------
  // DOWNLOAD APPLICATION PDF
  // -------------------------
  downloadApplicationPDF: async (id) => {
    try {
      const res = await axios.get(
        `${API_URL}/downloads/${id}/application`,
        {
          ...getAuthHeaders(),
          responseType: "blob",
        }
      );
      downloadHelper(res.data, `Application_${id}.pdf`);
    } catch (e) {
      await handleError(e);
    }
  },

  // -------------------------
  // DOWNLOAD INVOICE PDF
  // -------------------------
  downloadInvoicePDF: async (id) => {
    try {
      const res = await axios.get(
        `${API_URL}/downloads/${id}/invoice`,
        {
          ...getAuthHeaders(),
          responseType: "blob",
        }
      );
      downloadHelper(res.data, `Invoice_${id}.pdf`);
    } catch (e) {
      await handleError(e);
    }
  },

  // -------------------------
  // DOWNLOAD BOTH PDFs
  // -------------------------
  downloadBothPDFs: async (id) => {
    try {
      const app = await axios.get(
        `${API_URL}/downloads/${id}/application`,
        {
          ...getAuthHeaders(),
          responseType: "blob",
        }
      );
      downloadHelper(app.data, `Application_${id}.pdf`);

      await new Promise((r) => setTimeout(r, 400));

      const invoice = await axios.get(
        `${API_URL}/downloads/${id}/invoice`,
        {
          ...getAuthHeaders(),
          responseType: "blob",
        }
      );
      downloadHelper(invoice.data, `Invoice_${id}.pdf`);
    } catch (e) {
      await handleError(e);
    }
  },
};